import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ImageUploader } from "./ImageUploader";

const empty = {
  name: "", description: "", price_per_night: 1500, original_price: 2000,
  capacity: "2 Adults", bed: "1 Queen Bed", size: "210 sq.ft", ac: true,
  amenities: "Free Wi-Fi, Air Conditioning, Flat Screen TV", image: "", images: [], is_available: true,
};

export const RoomFormModal = ({ open, room, onClose, onSaved }) => {
  const isEdit = !!room;
  const [form, setForm] = useState(
    room ? { ...room, amenities: room.amenities.join(", "), images: room.images?.length ? room.images : room.image ? [room.image] : [] } : empty
  );
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateDescription = async () => {
    if (!form.name.trim()) { toast.error("Add a room name first"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/room-description", {
        name: form.name,
        capacity: form.capacity,
        bed: form.bed,
        size: form.size,
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        price_per_night: Number(form.price_per_night) || 0,
      });
      set("description", data.description);
      toast.success("Description generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate");
    } finally {
      setGenerating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      image: form.images[0] || "",
      price_per_night: Number(form.price_per_night),
      original_price: Number(form.original_price),
      amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
    };
    delete payload.id; delete payload.room_id;
    try {
      if (isEdit) await api.put(`/rooms/${room.room_id}`, payload);
      else await api.post("/rooms", payload);
      toast.success(isEdit ? "Room updated" : "Room added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="room-form-modal">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isEdit ? "Edit Room" : "Add Room"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="room-form-name" /></div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Description</Label>
              <button type="button" onClick={generateDescription} disabled={generating}
                data-testid="room-form-generate-description"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 disabled:opacity-50">
                {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                Generate with AI
              </button>
            </div>
            <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="room-form-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Price / night (₹)</Label>
              <Input type="number" required value={form.price_per_night} onChange={(e) => set("price_per_night", e.target.value)} data-testid="room-form-price" /></div>
            <div><Label className="text-xs">Original price (₹)</Label>
              <Input type="number" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} data-testid="room-form-original-price" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Capacity</Label><Input value={form.capacity} onChange={(e) => set("capacity", e.target.value)} /></div>
            <div><Label className="text-xs">Bed</Label><Input value={form.bed} onChange={(e) => set("bed", e.target.value)} /></div>
            <div><Label className="text-xs">Size</Label><Input value={form.size} onChange={(e) => set("size", e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Amenities (comma separated)</Label>
            <Input value={form.amenities} onChange={(e) => set("amenities", e.target.value)} data-testid="room-form-amenities" /></div>
          <div><Label className="text-xs">Room photos</Label>
            <ImageUploader images={form.images} onChange={(imgs) => set("images", imgs)} testId="room-form-images" /></div>
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.ac} onCheckedChange={(v) => set("ac", v)} /> AC Room</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_available} onCheckedChange={(v) => set("is_available", v)} data-testid="room-form-available" /> Available</label>
          </div>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-primary hover:bg-primary/90 gap-2" data-testid="room-form-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save Changes" : "Add Room"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
